/**
 * Auth context — single source of truth for the current session.
 *
 * Wraps the lower-level cognito client in src/lib/auth.ts so consumers
 * never import Cognito directly. Backend swap (better-auth, Auth0, etc.)
 * means rewriting lib/auth.ts only.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  completeNewPassword as cognitoCompleteNewPassword,
  currentSession,
  type Session,
  type NewPasswordChallenge,
} from "../lib/auth";

interface AuthState {
  status: "loading" | "authenticated" | "unauthenticated";
  session: Session | null;
  error: string | null;
  pendingChallenge: NewPasswordChallenge | null;
}

interface AuthContextValue extends AuthState {
  /** Returns true if authenticated, false if a challenge was raised. Throws on hard auth failure. */
  signIn: (email: string, password: string) => Promise<boolean>;
  completeNewPassword: (newPassword: string, email: string) => Promise<void>;
  clearChallenge: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    session: null,
    error: null,
    pendingChallenge: null,
  });

  // On mount, try to restore a session from localStorage.
  useEffect(() => {
    let cancelled = false;
    void currentSession().then((session) => {
      if (cancelled) return;
      setState(
        session
          ? {
              status: "authenticated",
              session,
              error: null,
              pendingChallenge: null,
            }
          : {
              status: "unauthenticated",
              session: null,
              error: null,
              pendingChallenge: null,
            },
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<boolean> => {
    setState((s) => ({ ...s, error: null }));
    try {
      const result = await cognitoSignIn(email, password);
      if (result.kind === "SESSION") {
        setState({
          status: "authenticated",
          session: result.session,
          error: null,
          pendingChallenge: null,
        });
        return true;
      }
      // NEW_PASSWORD_REQUIRED — stay unauthenticated, surface challenge to UI.
      setState({
        status: "unauthenticated",
        session: null,
        error: null,
        pendingChallenge: result,
      });
      return false;
    } catch (err) {
      setState({
        status: "unauthenticated",
        session: null,
        error: (err as Error).message,
        pendingChallenge: null,
      });
      throw err;
    }
  }, []);

  const completeNewPassword = useCallback(async (newPassword: string, email: string) => {
    // Snapshot the current challenge from state without rerunning the closure on every update.
    const challenge = await new Promise<NewPasswordChallenge | null>((resolve) => {
      setState((s) => {
        resolve(s.pendingChallenge);
        return s;
      });
    });
    if (!challenge) {
      throw new Error("No pending password challenge.");
    }
    try {
      const session = await cognitoCompleteNewPassword(challenge, newPassword, email);
      setState({
        status: "authenticated",
        session,
        error: null,
        pendingChallenge: null,
      });
    } catch (err) {
      setState((s) => ({ ...s, error: (err as Error).message }));
      throw err;
    }
  }, []);

  const clearChallenge = useCallback(() => {
    setState((s) => ({ ...s, pendingChallenge: null, error: null }));
  }, []);

  const signOut = useCallback(async () => {
    await cognitoSignOut();
    setState({
      status: "unauthenticated",
      session: null,
      error: null,
      pendingChallenge: null,
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, completeNewPassword, clearChallenge, signOut }),
    [state, signIn, completeNewPassword, clearChallenge, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** Current ID token for API calls — null while loading / unauthenticated. */
export function useIdToken(): string | null {
  const { session } = useAuth();
  return session?.idToken ?? null;
}
